import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { ArrowRightFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Button, Stack, Typography } from '@lyearn/ui';
import { useBreadcrumbContext } from '@/components/Breadcrumb/context';
import useNavigation from '@/hooks/useNavigation';
import useQueryParam, { StringParam } from '@/hooks/useQueryParam';
import useGoToHome from '@/modules/corporate/modules/course/hooks/useGoToHome';
import ButtonContainer from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/components/ButtonContainer';
import BadgePreviewModals from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/components/CompletionButton/BadgePreviewModals';
import { COMPLETE_CONFIG } from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/components/CompletionButton/config';
import { ConfigType } from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/components/CompletionButton/types';
import { useUpdateContent } from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/hooks/useUpdateContent';
import { PARENT_ENTITY_ID } from '@/modules/corporate/modules/exercise/views/Details/utils';
import { getNavigationQuery } from '@/modules/corporate/utils/getNavigationQuery';
import PointsPreviewModal from '@/modules/gamification/components/Points/PointsPreviewModal';
import corporateRoutes from '@/pages/corporate/routes';
import { ArticleContentType, PerformanceStatusEnum } from '@/types/schema';

import { ArticlePerformanceFragment } from '../../../../graphql/fragments/generated/ArticlePerformance';

const ARTICLE_COMPLETE_CONFIG: ConfigType = {
  ...COMPLETE_CONFIG,
  Description: {
    ...COMPLETE_CONFIG.Description,
    i18nKey: 'Article:ArticleCompletedDescription',
  },
};

const ArticleCompletionButton: React.FC<{
  articleId: string;
  article: ArticleContentType;
  progress?: number;
  refetchContentPerformance?: () => void;
  userBadges?: ArticlePerformanceFragment['userBadges'];
  points?: number;
}> = ({ articleId, progress, article, refetchContentPerformance, userBadges, points = 0 }) => {
  const { t } = useTranslation(['Article', 'Course']);

  const { isMobile } = useScreen();

  const [parentEntityId] = useQueryParam(PARENT_ENTITY_ID, StringParam);
  const { onBackToHome } = useGoToHome();
  const config = ARTICLE_COMPLETE_CONFIG;
  const navigation = useNavigation();
  const { breadcrumbUrl } = useBreadcrumbContext();

  const [isPointsModalOpen, setIsPointsModalOpen] = useState(true);

  const hasRefetched = useRef(false);

  useEffect(() => {
    if (progress === 100 && !hasRefetched.current) {
      refetchContentPerformance?.();
      hasRefetched.current = true;
    } else if ((progress || 0) < 100 && hasRefetched.current) {
      hasRefetched.current = false;
    }
  }, [progress, refetchContentPerformance]);

  const onClick = useCallback(
    (parentEntityId?: string | null) => {
      if (parentEntityId && typeof parentEntityId === 'string') {
        const query = getNavigationQuery(breadcrumbUrl);
        navigation.push(corporateRoutes.PathDetails(parentEntityId), { query });
        return;
      }

      onBackToHome();
    },
    [breadcrumbUrl, navigation, onBackToHome],
  );
  const articlePerformanceStatus = useMemo(() => {
    if (progress === 100) return PerformanceStatusEnum.Completed;
    return PerformanceStatusEnum.InProgress;
  }, [progress]);
  const { nextContentName, onNext } = useUpdateContent(
    parentEntityId ?? '',
    articleId,
    articlePerformanceStatus,
  );

  const initialArticleStatusRef = useRef(articlePerformanceStatus);

  if (!progress || progress !== 100) {
    return null;
  }

  const awardIds = userBadges?.map((badge) => badge.awardId);

  const currentBadges = article?.badges?.filter((badge) => awardIds?.includes(badge.id));

  const showBadgesOrPoints =
    initialArticleStatusRef.current !== PerformanceStatusEnum.Completed &&
    articlePerformanceStatus === PerformanceStatusEnum.Completed;

  return (
    <>
      <Box className="xs:px-18 mt-36 mb-36">
        <ButtonContainer {...config.Container} pointerEvents="none">
          <Stack className="flex flex-shrink" direction="col" justifyContent="between">
            <Typography
              className={config.Label.className}
              color={config.Label.color}
              variant="label-caps">
              {t(config.Label.i18nKey)}
            </Typography>
            <Typography
              className="mt-4 flex-shrink"
              color={config.Description.color}
              variant="heading-xs">
              {t(config.Description.i18nKey)}
            </Typography>
          </Stack>
          <Stack {...config.ActionsContainer} gapX={2}>
            <Button
              {...config.Button}
              appearance="inverse"
              variant={parentEntityId ? 'outlined' : 'filled'}
              onClick={() => onClick()}>
              {t(parentEntityId ? 'Course:GoToHome' : 'Course:BackToHome')}
            </Button>
            {parentEntityId ? (
              <Button
                {...config.Button}
                appearance="inverse"
                variant="filled"
                onClick={() => onClick(parentEntityId)}>
                {t('Course:BackToPath')}
              </Button>
            ) : null}
          </Stack>
        </ButtonContainer>
      </Box>
      {parentEntityId && onNext && (
        <Stack
          alignItems="center"
          className={cn('shadow-01 px-24 py-20 bg-bg-secondary mt-32', {
            'fixed bottom-0 z-50 w-full bg-white border-t border-border': isMobile,
          })}
          justifyContent="between"
          onClick={onNext}>
          {!isMobile && (
            <Stack alignItems="start" direction="col" gapY={4}>
              <Typography color="text-text-placeholder" variant="label-caps">
                {t('Common:NextContent')}
              </Typography>
              <Typography variant="heading-xs">{nextContentName}</Typography>
            </Stack>
          )}
          <Button
            EndIcon={ArrowRightFill}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            variant="filled">
            {t(isMobile ? `Common:NextContent` : `Common:ViewContent`)}
          </Button>
        </Stack>
      )}
      {showBadgesOrPoints && currentBadges?.length ? (
        <BadgePreviewModals badges={currentBadges} />
      ) : null}
      {showBadgesOrPoints && points > 0 ? (
        <PointsPreviewModal
          open={isPointsModalOpen}
          points={points}
          onClose={() => setIsPointsModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ArticleCompletionButton;
